<template>
  <v-row dense>
    <v-col cols="12" sm="6">
      <v-autocomplete
          :label="$t('classification.manufacturer')"
          :value="value.manufacturerId"
          :items="manufacturerItems"
          :loading="loading"
          item-text="name"
          item-value="id"
          clearable
          hide-details
          @change="changeManufacturer"
      >
        <template #item="{item}">
          <v-list-item-content>
            <v-list-item-title>{{ item.name }}</v-list-item-title>
          </v-list-item-content>
          <v-list-item-action-text>{{ item.chargingPoints }}</v-list-item-action-text>
        </template>
      </v-autocomplete>
    </v-col>
    <v-col cols="12" sm="6">
      <v-autocomplete
          :label="$t('classification.product-line-or-type')"
          :value="selectedKey"
          :items="lineItems"
          :loading="loading"
          :filter="filter"
          item-text="text"
          item-value="key"
          clearable
          hide-details
          @change="changeLine"
      >
        <template #item="{item}">
          <v-list-item-content :class="{'pl-4': item.kind === 'type'}">
            <v-list-item-title :class="{'font-weight-medium': item.kind === 'line', 'mono': item.kind === 'type'}">{{ item.text }}</v-list-item-title>
            <v-list-item-subtitle v-if="item.subtitle">{{ item.subtitle }}</v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-action-text>{{ item.chargingPoints }}</v-list-item-action-text>
        </template>
      </v-autocomplete>
    </v-col>
  </v-row>
</template>

<script>
import ChargingPointRepository from "@repository/ChargingPointRepository";

/**
 * Chercher des bornes par constructeur, puis par gamme ou par type : deux champs, valeur
 * `{manufacturerId, productLineId, chargingPointTypeId}`. Le second liste les gammes du constructeur choisi (de tous
 * sinon), chacune suivie de ses types ; choisir une gamme, c'est toutes ses déclinaisons.
 */
export default {
  model: {
    prop: 'value',
  },

  props: {
    value: {type: Object, default: () => ({manufacturerId: null, productLineId: null, chargingPointTypeId: null})},
  },

  data() {
    return {
      manufacturers: [],
      loading: false,
    }
  },

  computed: {
    // Un constructeur pas encore créé (type pas rangé) ne se choisit pas : il n'a pas d'identifiant.
    manufacturerItems() {
      return this.manufacturers.filter(manufacturer => manufacturer.id)
    },
    lineItems() {
      const items = []
      const manufacturers = this.value.manufacturerId ? this.manufacturers.filter(m => m.id === this.value.manufacturerId) : this.manufacturers
      for (const manufacturer of manufacturers) {
        for (const line of manufacturer.productLines) {
          if (line.id) {
            items.push({key: 'line:' + line.id, kind: 'line', text: line.name, subtitle: this.value.manufacturerId ? null : manufacturer.name, chargingPoints: line.chargingPoints, search: [line.name, manufacturer.name]})
          }
          // Une gamme d'un seul type : le type n'apporte rien de plus.
          if (line.types.length > 1 || !line.id) {
            for (const type of line.types) {
              items.push({key: 'type:' + type.id, kind: 'type', text: type.model || type.modelLabel || '?', subtitle: type.modelLabel && type.modelLabel !== type.model ? type.modelLabel : null, chargingPoints: type.chargingPoints, search: [type.model, type.modelLabel, line.name, manufacturer.name]})
            }
          }
        }
      }
      return items
    },
    selectedKey() {
      if (this.value.chargingPointTypeId) {
        return 'type:' + this.value.chargingPointTypeId
      }
      return this.value.productLineId ? 'line:' + this.value.productLineId : null
    },
  },

  mounted() {
    this.loading = true
    ChargingPointRepository.classification().then(manufacturers => {
      this.manufacturers = manufacturers
    }).catch(err => {
      this.notifyError(err)
    }).finally(() => {
      this.loading = false
    })
  },

  methods: {
    changeManufacturer(manufacturerId) {
      this.$emit('input', {manufacturerId: manufacturerId || null, productLineId: null, chargingPointTypeId: null})
    },

    changeLine(key) {
      const [kind, id] = (key || '').split(':')
      this.$emit('input', {
        manufacturerId: this.value.manufacturerId,
        productLineId: kind === 'line' ? id : null,
        chargingPointTypeId: kind === 'type' ? id : null,
      })
    },

    // Chaque mot doit se retrouver dans la gamme, le type ou le constructeur.
    filter(item, queryText) {
      const haystack = item.search.filter(v => v).join(' ').toLowerCase()
      return queryText.toLowerCase().split(/\s+/).filter(w => w).every(word => haystack.includes(word))
    },
  },
}
</script>

<style scoped>
.mono {
  font-family: monospace;
}
</style>
